/**
 * Footer component will be the main Footer Component for POC
 *
 * @module views/components/Footer
 * @memberof -Common
 */
import './Footer.scss';

import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useListenForGlobal } from '@ulta/core/hooks/useListenForGlobal/useListenForGlobal';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
/**
 * Represents a Footer component
 *
 * @method
 * @param {FooterProps} props - React properties passed from composition
 * @returns Footer
 */
export const Footer = React.forwardRef( ( props, _ ) => {
  const { footerTagLine, modules, copyrightInformation, secondaryFooterLinksWithIcon, secondaryFooterLinks } = props;
  const footerLinks = secondaryFooterLinksWithIcon || secondaryFooterLinks;

  if( !modules ){
    return null;
  }

  return (
    <footer className='Footer'
      role='contentinfo'
      id='FooterWrapper'
    >
      <div className='Footer__wrapper'>
        <div className='Footer__footerTagline'>
          { footerTagLine &&
            <UltaAsset { ...spreadProps( { ...footerTagLine } ) } />
          }
        </div>
        { modules?.map( ( i, index ) => {
          return (
            <AsyncComponent
              key={ `${i.id}:${index}` }
              { ...spreadProps( { ...i } ) }
            />
          );
        } ) }
      </div>
      { footerLinks && (
        <div className='Footer__bottomWrapper'>
          <div className='Footer__bottomWrapperRow'>
            <Text htmlTag='p'
              textStyle='body-3'
            >
              { copyrightInformation }
            </Text>

            <ul className='Footer__list'>
              { footerLinks.map( ( footerItem, index ) => {
                const clientActionType = footerItem?.linkAction?.clientActionType || footerItem?.clientActionType;
                const footerLinkLabel = footerItem?.linkAction?.label || footerItem?.label;
                const showSellMyInfoIcon = !!secondaryFooterLinks;
                return (
                  <li className='Footer__listItem'
                    key={ index }
                  >
                    { clientActionType === CLIENT_ACTION_TYPES.ONETRUST_CLICK ? (
                      <OneTrustLink
                        showSellMyInfoIcon={ showSellMyInfoIcon }
                        { ...spreadProps( { ...footerItem } ) }
                      />
                    ) : (
                      <Link_Huge
                        target='_blank'
                        className='Footer__Links'
                        url={ footerItem?.url }
                        tertiary
                        compact
                        action={ footerItem?.linkAction || footerItem }
                        iconName={ footerItem?.icon }
                        iconPosition={ 'before' }
                        iconsize={ 'xl' }
                        ariaLabel={ footerLinkLabel }
                      >
                        { footerLinkLabel }
                      </Link_Huge>
                    ) }
                  </li>
                );
              } ) }
            </ul>
          </div>
        </div>
      ) }
    </footer>
  );
} );

/**
 * Renders a OneTrustLink component.
 *
 * @param { Object } props - The component's properties.
 * @returns { Button } The rendered OneTrustLink component.
 */
export const OneTrustLink = ( { showSellMyInfoIcon, ...props } ) => {
  const footerItem = handleEmptyObjects( props );
  const [display, setDisplay] = useState( false );
  const { footerSellMyInfoLinkIcon } = useAppConfigContext();
  const buttonLabel = footerItem?.linkAction?.label || footerItem.label;
  const iconName = showSellMyInfoIcon && footerSellMyInfoLinkIcon ? footerSellMyInfoLinkIcon : footerItem?.icon;
  const action = footerItem?.linkAction || footerItem;

  useListenForGlobal( {
    watch: () => global.OneTrust?.ToggleInfoDisplay,
    callback: ( ) => {
      setDisplay( true );
    }
  } );

  if( !display ){
    return null;
  }

  return (
    <Button
      variant='unstyled'
      className={ classNames(
        'Footer__ot-sdk-show-settings',
        { 'Footer__LinkIcon': footerSellMyInfoLinkIcon }
      ) }
      action={ action }
      label={ buttonLabel }
      ariaLabel={ buttonLabel }
      onClick={ global.OneTrust.ToggleInfoDisplay }
      title={ buttonLabel }
      value={ buttonLabel }
      iconName={ iconName }
      iconPosition={ 'before' }
      iconSize={ 'xl' }
    />
  );
};

/**
 * Property type definitions
 * @typedef FooterProps
 * @type {object}
 * @property {string} id - sets the content ID for the Footer
 */
export const propTypes = {
  id: PropTypes.string
};

Footer.propTypes = propTypes;
Footer.displayName = 'Footer';

export default Footer;
